import { Link } from 'react-router-dom';
import { Instagram, Youtube, Music2, MapPin, Phone, Mail } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { FOOTER } from '@/constants/testIds';

export const Footer = () => {
  const config = useSiteConfig();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0A0A0A] border-t border-[hsl(var(--lxra-border))]" data-testid={FOOTER.root}>
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-14 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div>
            <Logo />
            <p className="mt-4 text-sm text-[hsl(var(--lxra-muted))] max-w-xs">
              Capturing moments. Creating memories. Premium photography &amp; videography across the DMV.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a
                href={config.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid={FOOTER.instagram}
                className="text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="#"
                aria-label="YouTube"
                data-testid={FOOTER.youtube}
                className="text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href="#"
                aria-label="TikTok"
                data-testid={FOOTER.tiktok}
                className="text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200"
              >
                <Music2 className="h-5 w-5" />
              </a>
            </div>

            <div className="mt-8">
              <p className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--lxra-gold))] mb-3">Join the mailing list</p>
              <NewsletterSignup />
            </div>
          </div>

          <div className="lg:text-center">
            <p className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--lxra-gold))]">Ready to capture your story?</p>
            <h3 className="font-display text-2xl sm:text-3xl mt-2 tracking-[0.04em] text-[hsl(var(--lxra-fg))]">
              BOOK YOUR SESSION TODAY.
            </h3>
            <Link to="/book" data-testid={FOOTER.bookNow}>
              <button className="mt-5 inline-flex items-center h-11 px-6 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] hover:-translate-y-0.5 transition-transform duration-200 text-xs uppercase tracking-[0.1em] font-semibold">
                Book Now
              </button>
            </Link>
          </div>

          <div className="space-y-3 text-sm text-[hsl(var(--lxra-muted))] lg:justify-self-end">
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-[hsl(var(--lxra-gold))] flex-shrink-0" />
              <span>{config.service_area}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-[hsl(var(--lxra-gold))] flex-shrink-0" />
              <a href={`tel:${config.phone}`} className="hover:text-[hsl(var(--lxra-gold))] transition-colors">{config.phone}</a>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-[hsl(var(--lxra-gold))] flex-shrink-0" />
              <a href={`mailto:${config.email}`} className="hover:text-[hsl(var(--lxra-gold))] transition-colors break-all">{config.email}</a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[hsl(var(--lxra-border))] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[hsl(var(--lxra-muted))] lg:pr-28">
          <span>&copy; {year} LXRA Studios. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <Link to="/galleries" className="hover:text-[hsl(var(--lxra-gold))] transition-colors">Galleries</Link>
            <Link to="/services" className="hover:text-[hsl(var(--lxra-gold))] transition-colors">Services</Link>
            <Link to="/journal" className="hover:text-[hsl(var(--lxra-gold))] transition-colors">Journal</Link>
            <Link to="/gear" data-testid={FOOTER.gearLibraryLink} className="hover:text-[hsl(var(--lxra-gold))] transition-colors">Gear Guide</Link>
            <Link to="/contact" className="hover:text-[hsl(var(--lxra-gold))] transition-colors">Contact</Link>
            <Link to="/login" data-testid={FOOTER.clientPortalLink} className="hover:text-[hsl(var(--lxra-gold))] transition-colors">Client Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
